import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Trophy } from "lucide-react";
import { Avatar, RankChip } from "./ui";
import { RankingRow } from "../data/types";
import { usePortalLink } from "../utils/portalLink";
import { formatPercent } from "../utils/score";

/*
 * Rankings, one board per rifle class.
 *
 * The classes do not share a scoring scale — Target Rifle scores 5 per shot with
 * a V centre, F-Class 6 with an X, and Sporter is a different discipline again.
 * Ranked together, an F-Class shooter outranks a Target Rifle shooter on the
 * strength of a higher maximum rather than better shooting, and the "winner" is
 * whoever happens to shoot the class with the highest possible score.
 *
 * So each class is its own board with its own number one. That is the placing a
 * competitor is actually awarded, and the one worth showing.
 *
 * Grades work differently: a grade is a division WITHIN a class (A, B and C
 * shooters all shoot Target Rifle), so grade boards are grouped by class first
 * and then by grade — never by grade alone, which would compare a TR A-grade
 * against an F-Class A-grade.
 */

export interface RankingBoard {
  rifleClass: string;
  /** Set when boards are split by grade as well. */
  grade?: string;
  rows: RankingRow[];
}

/** Groups rows into boards and ranks within each, strongest class first. */
/*
 * Board order. Target Rifle, then the F-Class faces, then anything else.
 * They are separate disciplines rather than divisions of one — Target Rifle and
 * F-TR are shot on different faces to different rules — so each holds its own
 * ladder and a competitor entered in both appears on both.
 */
const CLASS_ORDER = ["TR", "FTR", "FO", "FS", "SH"];
export function buildBoards(rows: RankingRow[], byGrade = false): RankingBoard[] {
  const map = new Map<string, RankingRow[]>();
  rows.forEach((r) => {
    const cls = r.rifleClass || "Open";
    // An ungraded competitor keeps an empty grade, so the class's board is
    // headed by the class alone rather than by an invented division.
    const key = byGrade ? `${cls}|||${r.grade ?? ""}` : cls;
    map.set(key, [...(map.get(key) || []), r]);
  });

  return Array.from(map.entries())
    .map(([key, members]) => {
      const [rifleClass, grade] = key.split("|||");
      return {
        rifleClass,
        grade,
        // Ranked on average, then best — the same order the API ranks on.
        rows: [...members]
          .sort((a, b) => (b.avgPercent ?? 0) - (a.avgPercent ?? 0) || (b.bestPercent ?? 0) - (a.bestPercent ?? 0))
          .map((r, i) => ({ ...r, rank: i + 1 })),
      };
    })
    /*
     * A fixed board order, not one decided by whoever shot best.
     *
     * Sorting the boards by their leader's percentage moved Target Rifle up and
     * down the page week to week, and put whichever class happened to hold a
     * clean card on top. Target Rifle leads because it is the discipline the
     * Association is built around; the F-Class faces follow, then anything else
     * alphabetically. Grades stay in order within their class.
     */
    .sort((a, b) => {
      if (a.rifleClass === b.rifleClass) return String(a.grade).localeCompare(String(b.grade));
      const ia = CLASS_ORDER.indexOf(a.rifleClass);
      const ib = CLASS_ORDER.indexOf(b.rifleClass);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return a.rifleClass.localeCompare(b.rifleClass);
    });
}

/*
 * How deep a board opens.
 *
 * A class with five thousand ranked competitors printed five thousand rows, and
 * every other class on the page moved out of reach below them. Ten is the depth
 * worth reading at a glance; the rest of the class is one click away and opens
 * in place, so nobody has to leave the page to see it.
 */
const BOARD_DEPTH = 10;

function Board({ board, showGrade }: { board: RankingBoard; showGrade: boolean }) {
  const link = usePortalLink();
  const [showAll, setShowAll] = useState(false);
  const hidden = Math.max(0, board.rows.length - BOARD_DEPTH);
  const visible = showAll ? board.rows : board.rows.slice(0, BOARD_DEPTH);
  /*
   * Only a shooter who has actually scored leads the class. A competitor on the
   * register with no results sorts to the top of a class nobody else has shot,
   * and naming them the winner on an average of zero is worse than saying
   * nothing.
   */
  const leader = (board.rows[0]?.avgPercent ?? 0) > 0 ? board.rows[0] : null;

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-slate-50 px-4 py-2.5">
        <h3 className="flex items-center gap-2 text-[15px] font-extrabold text-ink">
          {board.rifleClass}
          {board.grade ? <span className="text-muted">· Grade {board.grade}</span> : null}
          <span className="text-[13px] font-semibold text-muted">
            {board.rows.length} {board.rows.length === 1 ? "shooter" : "shooters"}
          </span>
        </h3>
        {/* The class winner, named rather than left to be worked out from row 1. */}
        {leader && (
          <span className="flex items-center gap-1.5 text-[13px] font-bold text-success">
            <Trophy size={14} /> {leader.shooter}
            <span className="font-semibold text-muted">{formatPercent(leader.avgPercent)} overall</span>
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="border-b border-line">
              <th className="th">Rank</th>
              <th className="th">Shooter</th>
              <th className="th">Club</th>
              {showGrade && <th className="th">Grade</th>}
              <th className="th text-right">Events</th>
              <th className="th text-right">Average Score</th>
              {/* The competitor's own best single result, not the field's. */}
              <th className="th text-right">Personal Best</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {visible.map((r, i) => (
              <tr
                key={r.sid}
                onClick={() => window.open(link(`/shooters/${r.sid}`), "_blank", "noopener")}
                className={`cursor-pointer transition-colors hover:bg-primary-soft/40 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}
              >
                <td className="td"><RankChip rank={r.rank} /></td>
                <td className="td">
                  <div className="flex items-center gap-3">
                    <Avatar name={r.shooter} />
                    <div>
                      <Link to={link(`/shooters/${r.sid}`)} target="_blank" rel="noopener noreferrer" className="font-bold text-ink hover:text-primary">
                        {r.shooter}
                      </Link>
                      <div className="text-xs text-muted">SID: {r.sid}</div>
                    </div>
                  </div>
                </td>
                <td className="td text-slate-600">{r.club}</td>
                {showGrade && <td className="td">{r.grade}</td>}
                <td className="td text-right">{r.competitions}</td>
                <td className="td text-right font-bold">{formatPercent(r.avgPercent)}</td>
                <td className="td text-right font-bold text-primary">{formatPercent(r.bestPercent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Opening the class shows the whole class, in place rather than elsewhere. */}
      {!!hidden && (
        <button
          onClick={() => setShowAll((v) => !v)}
          className="flex w-full items-center justify-center gap-1.5 border-t border-line bg-slate-50 px-4 py-2.5 text-[13px] font-bold text-primary transition-colors hover:bg-primary-soft"
        >
          <ChevronDown size={15} className={"transition-transform " + (showAll ? "rotate-180" : "")} />
          {showAll
            ? "Show top " + BOARD_DEPTH + " only"
            : "Show all " + board.rows.length + " in " + board.rifleClass + (board.grade ? " Grade " + board.grade : "")}
        </button>
      )}
    </div>
  );
}

export default function ClassRankingBoards({
  rows,
  byGrade = false,
  emptyMessage = "No rankings yet — import a shoot to populate them.",
}: {
  rows: RankingRow[];
  /** Split each class into its grades as well. */
  byGrade?: boolean;
  emptyMessage?: string;
}) {
  const boards = useMemo(() => buildBoards(rows, byGrade), [rows, byGrade]);
  if (!boards.length) {
    return <div className="card p-10 text-center text-sm text-muted">{emptyMessage}</div>;
  }
  return (
    <div className="space-y-4">
      {boards.map((b) => (
        <Board key={`${b.rifleClass}-${b.grade ?? ""}`} board={b} showGrade={!byGrade} />
      ))}
    </div>
  );
}

/** The number one in each class — the winners, side by side. */
export function ClassLeaders({ rows }: { rows: RankingRow[] }) {
  const link = usePortalLink();
  const boards = useMemo(() => buildBoards(rows), [rows]);
  // A class where nobody has scored yet has no leader to name.
  const leaders = boards
    .map((b) => ({ rifleClass: b.rifleClass, leader: b.rows[0] }))
    .filter((x) => x.leader && (x.leader.avgPercent ?? 0) > 0);

  if (!leaders.length) {
    return (
      <div className="card p-4">
        <h4 className="flex items-center gap-2 text-sm font-extrabold text-ink">
          <Trophy size={16} className="text-muted" /> Class Leaders
        </h4>
        <p className="mt-3 text-[13px] leading-snug text-muted">Leaders appear once a shoot has been imported.</p>
      </div>
    );
  }

  return (
    <div className="card border-success/30 bg-success-soft/60 p-4">
      <h4 className="flex items-center gap-2 text-sm font-extrabold text-ink">
        <Trophy size={16} className="text-success" /> Class Leaders
      </h4>
      <p className="mt-1 text-xs text-muted">One winner per class — the classes are not comparable.</p>
      <div className="mt-3 space-y-3">
        {leaders.map(({ rifleClass, leader }) => (
          <div key={rifleClass} className="flex items-center gap-3 border-t border-success/20 pt-3 first:border-0 first:pt-0">
            <Avatar name={leader.shooter} size={40} />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-extrabold uppercase tracking-wide text-success">{rifleClass}</div>
              <Link to={link(`/shooters/${leader.sid}`)} target="_blank" rel="noopener noreferrer" className="block truncate text-[14px] font-bold text-ink hover:text-primary">
                {leader.shooter}
              </Link>
              <div className="text-xs text-muted">
                {leader.club} · {formatPercent(leader.avgPercent)} overall · personal best {formatPercent(leader.bestPercent)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
